'use client';

import { c as _c } from "react/compiler-runtime";
import { suggestedAlertBalanceTrons, suggestedCaps, suggestedThresholdTrons } from '@shared/all/domains/autoTopUp';
import { tronsToCents } from '@shared/all/domains/products';
import { formatUSD } from '@shared/utils/helpers/formatCurrency';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import toast from '@shared/utils/helpers/toast';
import useProducts from '@shared/utils/hooks/useProducts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useReducer } from 'react';
import { client } from '../../utils/trpc';
import Button from '../common/Button';
import Dropdown from '../UI/DropDown';
import Input from '../UI/Input';
import Switch from '../UI/Switch';

// Settings > Billing. Auto top-up and every low-balance alert live on ONE
// screen: they answer the same question ("what happens when I run out?"), and
// splitting them would let someone turn off the thing that was quietly
// protecting them. Reached from the Trons page, which links here rather than
// embedding the form — buying trons and configuring a recurring charge are
// different intents, and the second one belongs with the account settings.
//
// STRIPE ONLY. Apple and Google forbid charging for digital goods outside IAP,
// so this card is web-only by necessity, not by choice.

type State = {
  enabled: boolean;
  productId: number;
  thresholdTrons: number;
  dailyCapCents: number;
  monthlyCapCents: number;
  alertOnTopUp: boolean;
  alertBalanceTrons: number | null;
  alertNearCap: boolean;
};

// One reducer rather than eight useStates (see CLAUDE.md: max 5).
const reducer = (state: State, patch: Partial<State>): State => ({
  ...state,
  ...patch
});
const fetchAutoTopUp = () => unwrapFromResult(client.autoTopUp.get.query());
type AutoTopUpData = Awaited<ReturnType<typeof fetchAutoTopUp>>;
type TronPackage = ReturnType<typeof useProducts>['products'][number];

/**
 * Waits for the data before mounting the form.
 *
 * The form seeds its reducer ONCE, from the saved config or from defaults
 * derived off the first package. Mounting it while either is still loading
 * seeded it with `productId: 0` and a threshold computed from a zero-tron
 * package — and a reducer's initial value is never re-read, so the form kept
 * those empty numbers for the life of the component. The old shape hid this
 * behind an early `return null` that ran AFTER useReducer, which stops the
 * render but not the seeding. Gating the mount is the fix; an Effect that
 * re-seeds afterwards would fight the user's own edits (see CLAUDE.md).
 */
const Billing = () => {
  const $ = _c(6);
  const {
    products
  } = useProducts(client);
  let t0;
  if ($[0] !== products) {
    t0 = products.filter(_temp);
    $[0] = products;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const packages = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryKey: ["autoTopUp"],
      queryFn: fetchAutoTopUp
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data
  } = useQuery(t1);
  if (!data || packages.length === 0) {
    return null;
  }
  let t2;
  if ($[3] !== data || $[4] !== packages) {
    t2 = <BillingForm data={data} packages={packages} />;
    $[3] = data;
    $[4] = packages;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
const BillingForm = t0 => {
  const $ = _c(113);
  const {
    data,
    packages
  } = t0;
  const queryClient = useQueryClient();
  const firstPackage = packages[0];
  const t1 = firstPackage?.id ?? 0;
  const t2 = Number(firstPackage?.tron?.quantity ?? 0);
  let t3;
  if ($[0] !== t2) {
    t3 = suggestedThresholdTrons(t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = firstPackage?.price ?? 0;
  let t5;
  if ($[2] !== t4) {
    t5 = suggestedCaps(t4);
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== t1 || $[5] !== t3 || $[6] !== t5) {
    t6 = {
      enabled: false,
      productId: t1,
      thresholdTrons: t3,
      ...t5,
      alertOnTopUp: true,
      alertBalanceTrons: null,
      alertNearCap: true
    };
    $[4] = t1;
    $[5] = t3;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const defaults = t6;
  const [form, patch] = useReducer(reducer, data.config ?? defaults);
  let t7;
  if ($[8] !== form.alertBalanceTrons || $[9] !== form.thresholdTrons) {
    t7 = form.alertBalanceTrons ?? suggestedAlertBalanceTrons(form.thresholdTrons);
    $[8] = form.alertBalanceTrons;
    $[9] = form.thresholdTrons;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const lastAlertBalance = t7;
  let t8;
  if ($[11] !== form) {
    t8 = () => unwrapFromResult(client.autoTopUp.set.mutate(form));
    $[11] = form;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== queryClient) {
    t9 = async () => {
      toast("Trons auto top-up saved", undefined, "success");
      await queryClient.invalidateQueries({
        queryKey: ["autoTopUp"]
      });
    };
    $[13] = queryClient;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t8 || $[16] !== t9) {
    t10 = {
      mutationFn: t8,
      onSuccess: t9,
      onError: _temp2
    };
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const save = useMutation(t10);
  let t11;
  if ($[18] !== form.productId || $[19] !== packages) {
    let t12;
    if ($[21] !== form.productId) {
      t12 = p => p.id === form.productId;
      $[21] = form.productId;
      $[22] = t12;
    } else {
      t12 = $[22];
    }
    t11 = packages.find(t12);
    $[18] = form.productId;
    $[19] = packages;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const selected = t11;
  const packageTrons = Number(selected?.tron?.quantity ?? 0);
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div><h3 className="font-semibold text-textPrimary">Trons auto top-up</h3><p className="mt-1 text-sm text-textSecondary">Buy more trons automatically when you run low, so a long task never stops halfway.</p></div>;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== form.enabled) {
    t13 = () => patch({
      enabled: !form.enabled
    });
    $[24] = form.enabled;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== form.enabled || $[27] !== t13) {
    t14 = <div className="flex items-start justify-between gap-4">{t12}<Switch state={form.enabled} onChange={t13} /></div>;
    $[26] = form.enabled;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const t15 = `anim-collapse ${data.hasSavedCard ? "" : "anim-collapse-open"}`;
  let t16;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <p className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-sm text-textSecondary">To turn this on, tick <strong>Save my card for auto top-up</strong> on your next purchase. We can only charge a card you have agreed we may use while you are away.</p>;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t15) {
    t17 = <div className={t15}>{t16}</div>;
    $[30] = t15;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  const t18 = `anim-collapse ${form.enabled ? "anim-collapse-open" : ""}`;
  let t19;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <p className={sectionLabel}>Top-up</p>;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== packages) {
    t20 = packages.map(_temp3);
    $[33] = packages;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  const t21 = String(form.productId);
  let t22;
  if ($[35] !== packages) {
    t22 = option => {
      if (!option) {
        return;
      }
      const id = Number(option.value);
      const pkg = packages.find(p_1 => p_1.id === id);
      patch({
        productId: id,
        thresholdTrons: suggestedThresholdTrons(Number(pkg?.tron?.quantity ?? 0)),
        ...suggestedCaps(pkg?.price ?? 0)
      });
    };
    $[35] = packages;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] !== t20 || $[38] !== t21 || $[39] !== t22) {
    t23 = <Dropdown options={t20} selectedValue={t21} onSelect={t22} placeholder="Buy" />;
    $[37] = t20;
    $[38] = t21;
    $[39] = t22;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== packageTrons) {
    t24 = packageTrons.toLocaleString();
    $[41] = packageTrons;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  let t25;
  if ($[43] !== t24) {
    t25 = <span className={`${helperClass} text-textSecondary`}>Buys {t24} trons</span>;
    $[43] = t24;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  let t26;
  if ($[45] !== t23 || $[46] !== t25) {
    t26 = <div className="flex min-w-0 flex-col gap-1">{t23}{t25}</div>;
    $[45] = t23;
    $[46] = t25;
    $[47] = t26;
  } else {
    t26 = $[47];
  }
  let t27;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = v => patch({
      thresholdTrons: v
    });
    $[48] = t27;
  } else {
    t27 = $[48];
  }
  let t28;
  if ($[49] !== form.thresholdTrons || $[50] !== packageTrons) {
    t28 = packageTrons > 0 && form.thresholdTrons >= packageTrons ? `Must be under ${packageTrons.toLocaleString()} trons` : undefined;
    $[49] = form.thresholdTrons;
    $[50] = packageTrons;
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  let t29;
  if ($[52] !== form.thresholdTrons || $[53] !== t28) {
    t29 = <TronField label="Top up below" value={form.thresholdTrons} onChange={t27} hint={t28} />;
    $[52] = form.thresholdTrons;
    $[53] = t28;
    $[54] = t29;
  } else {
    t29 = $[54];
  }
  let t30;
  if ($[55] !== t26 || $[56] !== t29) {
    t30 = <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{t26}{t29}</div>;
    $[55] = t26;
    $[56] = t29;
    $[57] = t30;
  } else {
    t30 = $[57];
  }
  let t31;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = on => patch({
      alertOnTopUp: on
    });
    $[58] = t31;
  } else {
    t31 = $[58];
  }
  let t32;
  if ($[59] !== form.alertOnTopUp) {
    t32 = <Toggle checked={form.alertOnTopUp} onChange={t31} label="Tell me each time you top me up" />;
    $[59] = form.alertOnTopUp;
    $[60] = t32;
  } else {
    t32 = $[60];
  }
  let t33;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <p className={`${sectionLabel} mb-3`}>Limits</p>;
    $[61] = t33;
  } else {
    t33 = $[61];
  }
  let t34;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = v_0 => patch({
      dailyCapCents: v_0
    });
    $[62] = t34;
  } else {
    t34 = $[62];
  }
  let t35;
  if ($[63] !== data.spentTodayCents || $[64] !== form.dailyCapCents) {
    t35 = <MoneyField label="Daily limit" value={form.dailyCapCents} onChange={t34} spent={data.spentTodayCents} />;
    $[63] = data.spentTodayCents;
    $[64] = form.dailyCapCents;
    $[65] = t35;
  } else {
    t35 = $[65];
  }
  let t36;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = v_1 => patch({
      monthlyCapCents: v_1
    });
    $[66] = t36;
  } else {
    t36 = $[66];
  }
  let t37;
  if ($[67] !== data.spentThisMonthCents || $[68] !== form.monthlyCapCents) {
    t37 = <MoneyField label="Monthly limit" value={form.monthlyCapCents} onChange={t36} spent={data.spentThisMonthCents} />;
    $[67] = data.spentThisMonthCents;
    $[68] = form.monthlyCapCents;
    $[69] = t37;
  } else {
    t37 = $[69];
  }
  let t38;
  if ($[70] !== t35 || $[71] !== t37) {
    t38 = <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{t35}{t37}</div>;
    $[70] = t35;
    $[71] = t37;
    $[72] = t38;
  } else {
    t38 = $[72];
  }
  let t39;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = on_0 => patch({
      alertNearCap: on_0
    });
    $[73] = t39;
  } else {
    t39 = $[73];
  }
  let t40;
  if ($[74] !== form.alertNearCap) {
    t40 = <div className="pt-2"><Toggle checked={form.alertNearCap} onChange={t39} label="Tell me when I'm near one of these limits" /></div>;
    $[74] = form.alertNearCap;
    $[75] = t40;
  } else {
    t40 = $[75];
  }
  let t41;
  if ($[76] !== t38 || $[77] !== t40) {
    t41 = <div className="border-t border-ds-border-standard pt-3">{t33}{t38}{t40}</div>;
    $[76] = t38;
    $[77] = t40;
    $[78] = t41;
  } else {
    t41 = $[78];
  }
  let t42;
  if ($[79] !== t30 || $[80] !== t32 || $[81] !== t41) {
    t42 = <div className="flex flex-col gap-3 pt-3">{t19}{t30}{t32}{t41}</div>;
    $[79] = t30;
    $[80] = t32;
    $[81] = t41;
    $[82] = t42;
  } else {
    t42 = $[82];
  }
  let t43;
  if ($[83] !== t18 || $[84] !== t42) {
    t43 = <div className={t18}>{t42}</div>;
    $[83] = t18;
    $[84] = t42;
    $[85] = t43;
  } else {
    t43 = $[85];
  }
  let t44;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <p className={sectionLabel}>Balance</p>;
    $[86] = t44;
  } else {
    t44 = $[86];
  }
  const t45 = form.alertBalanceTrons !== null;
  let t46;
  if ($[87] !== form.thresholdTrons) {
    t46 = on_1 => patch({
      alertBalanceTrons: on_1 ? suggestedAlertBalanceTrons(form.thresholdTrons) : null
    });
    $[87] = form.thresholdTrons;
    $[88] = t46;
  } else {
    t46 = $[88];
  }
  let t47;
  if ($[89] !== t45 || $[90] !== t46) {
    t47 = <Toggle checked={t45} onChange={t46} label="Tell me when my tron balance gets low" />;
    $[89] = t45;
    $[90] = t46;
    $[91] = t47;
  } else {
    t47 = $[91];
  }
  const t48 = `anim-collapse ${form.alertBalanceTrons !== null ? "anim-collapse-open" : ""}`;
  const t49 = form.alertBalanceTrons ?? lastAlertBalance;
  let t50;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = v_2 => patch({
      alertBalanceTrons: v_2
    });
    $[92] = t50;
  } else {
    t50 = $[92];
  }
  let t51;
  if ($[93] !== t49) {
    t51 = <div className="pt-1 sm:max-w-[calc(50%-0.375rem)]"><TronField label="Warn me at" value={t49} onChange={t50} /></div>;
    $[93] = t49;
    $[94] = t51;
  } else {
    t51 = $[94];
  }
  let t52;
  if ($[95] !== t48 || $[96] !== t51) {
    t52 = <div className={t48}>{t51}</div>;
    $[95] = t48;
    $[96] = t51;
    $[97] = t52;
  } else {
    t52 = $[97];
  }
  let t53;
  if ($[98] !== t47 || $[99] !== t52) {
    t53 = <div className="mt-3 flex flex-col gap-2 border-t border-ds-border-standard pt-3">{t44}{t47}{t52}</div>;
    $[98] = t47;
    $[99] = t52;
    $[100] = t53;
  } else {
    t53 = $[100];
  }
  const t54 = save.isPending ? "Saving\u2026" : "Save";
  let t55;
  if ($[101] !== save) {
    t55 = () => save.mutate();
    $[101] = save;
    $[102] = t55;
  } else {
    t55 = $[102];
  }
  let t56;
  if ($[103] !== save.isPending || $[104] !== t54 || $[105] !== t55) {
    t56 = <Button classNames="mt-3 self-start" text={t54} onClick={t55} disabled={save.isPending} />;
    $[103] = save.isPending;
    $[104] = t54;
    $[105] = t55;
    $[106] = t56;
  } else {
    t56 = $[106];
  }
  let t57;
  if ($[107] !== t14 || $[108] !== t17 || $[109] !== t43 || $[110] !== t53 || $[111] !== t56) {
    t57 = <div className="mx-auto my-4 flex w-full max-w-[660px] flex-col rounded-xl border border-solid border-ds-border-standard bg-background-secondary p-4 md:p-5">{t14}{t17}{t43}{t53}{t56}</div>;
    $[107] = t14;
    $[108] = t17;
    $[109] = t43;
    $[110] = t53;
    $[111] = t56;
    $[112] = t57;
  } else {
    t57 = $[112];
  }
  return t57;
};
const Toggle = t0 => {
  const $ = _c(11);
  const {
    checked,
    onChange,
    label
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="text-sm text-textSecondary">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== checked || $[3] !== onChange) {
    t2 = () => onChange(!checked);
    $[2] = checked;
    $[3] = onChange;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== checked || $[6] !== t2) {
    t3 = <Switch state={checked} onChange={t2} />;
    $[5] = checked;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t3) {
    t4 = <div className="flex items-center justify-between gap-3">{t1}{t3}</div>;
    $[8] = t1;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};

// Both fields put their name in the site Input's floating label and everything
// else on ONE helper line underneath. The old shape stacked three rows per
// field — a bold label, the control, then a hint — which is what made the card
// so tall. The unit lives in the label because the helper line is needed for
// the meaning of the number, not for restating what it is.
const helperClass = 'block truncate text-xs';

// Section headings, in the pattern CrossPostModal already uses. Small and quiet
// on purpose: they exist to say WHICH setting an alert belongs to, not to
// compete with the card's own title.
const sectionLabel = 'text-xs font-medium uppercase tracking-wider text-textSecondary';

/** Trons in, with the dollar value under it so the number means something. */
const TronField = t0 => {
  const $ = _c(16);
  const {
    label,
    value,
    onChange,
    hint
  } = t0;
  const t1 = `${label} (trons)`;
  let t2;
  if ($[0] !== onChange) {
    t2 = v => onChange(Number(v));
    $[0] = onChange;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t1 || $[3] !== t2 || $[4] !== value) {
    t3 = <Input type="number" label={t1} value={value} onChange={t2} />;
    $[2] = t1;
    $[3] = t2;
    $[4] = value;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = `${helperClass} ${hint ? "text-amber-400" : "text-textSecondary"}`;
  let t5;
  if ($[6] !== hint || $[7] !== value) {
    t5 = hint ?? `${formatUSD(tronsToCents(value))} of AI`;
    $[6] = hint;
    $[7] = value;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== hint || $[10] !== t4 || $[11] !== t5) {
    t6 = <span className={t4} title={hint}>{t5}</span>;
    $[9] = hint;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t3 || $[14] !== t6) {
    t7 = <div className="flex min-w-0 flex-col gap-1">{t3}{t6}</div>;
    $[13] = t3;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};

/** Dollars in, cents out. 0 means no ceiling, which we say out loud. */
const MoneyField = t0 => {
  const $ = _c(15);
  const {
    label,
    value,
    onChange,
    spent
  } = t0;
  const t1 = `${label} ($)`;
  const t2 = value / 100;
  let t3;
  if ($[0] !== onChange) {
    t3 = v => onChange(Math.round(Number(v) * 100));
    $[0] = onChange;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== t1 || $[3] !== t2 || $[4] !== t3) {
    t4 = <Input type="number" label={t1} value={t2} onChange={t3} />;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = `${helperClass} ${value === 0 ? "text-amber-400" : "text-textSecondary"}`;
  let t6;
  if ($[6] !== spent || $[7] !== value) {
    t6 = value === 0 ? "No limit \u2014 set one" : `${formatUSD(spent)} used`;
    $[6] = spent;
    $[7] = value;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = <span className={t5}>{t6}</span>;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t4 || $[13] !== t7) {
    t8 = <div className="flex min-w-0 flex-col gap-1">{t4}{t7}</div>;
    $[12] = t4;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return t8;
};
export default Billing;
function _temp(p) {
  return p.type === "TRON" && p.purchasable;
}
function _temp2(error) {
  return toast(error.message, undefined, "error");
}
function _temp3(p_0) {
  return {
    value: String(p_0.id),
    label: `${p_0.title} — ${formatUSD(p_0.price)}`
  };
}